import React, { useState, useRef, useEffect } from 'react';
import {
  UserIcon,
  ComputerDesktopIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { ConversationAnalytics, VisitorJourneyData } from '../../services/analyticsService';

interface MessageTimelineProps {
  conversation: ConversationAnalytics;
  visitorJourney: VisitorJourneyData;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: any;
}

const MessageTimeline: React.FC<MessageTimelineProps> = ({ conversation, visitorJourney }) => {
  const [highlightedMessage, setHighlightedMessage] = useState<number | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Parse messages from the conversation
  const messages: Message[] = conversation.messages || [];

  // Find conversion point (if lead exists, find when it happened)
  const conversionPoint = conversation.lead_id ? findConversionPoint(messages, conversation.context) : null;

  function findConversionPoint(messages: Message[], context: any): number | null {
    if (!context) return null;

    // Look for the message where all required info was collected
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message.role === 'user' && message.content) {
        // Check if this message could have completed the lead
        const hasEmailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(message.content);
        const hasPhonePattern = /(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/.test(message.content);
        const hasPhoneConsent = /\b(yes|sure|okay|ok|sounds good|that works|phone|text)\b/i.test(message.content);

        if (hasEmailPattern || hasPhonePattern || hasPhoneConsent) {
          return i;
        }
      }
    }
    return null;
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getMessageInsights = (message: Message, index: number) => {
    const insights = [];

    if (message.role === 'user') {
      const content = message.content.toLowerCase();

      // Detect key information being provided
      if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(message.content)) {
        insights.push({ type: 'email', label: 'Email provided' });
      }

      if (/\b(my name is|i'm|call me)\s+([a-z]+)/i.test(message.content)) {
        insights.push({ type: 'name', label: 'Name provided' });
      }

      if (/\b(autism|adhd|add|asperger|learning|disability|special needs|iep|504)\b/i.test(content)) {
        insights.push({ type: 'special_needs', label: 'Special needs mentioned' });
      }

      if (/\b(son|daughter|child|kid|boy|girl|he|she|they|age|years old|grade)\b/i.test(content)) {
        insights.push({ type: 'child_info', label: 'Child information' });
      }

      if (/\b(yes|sure|okay|ok|sounds good|that works)\b/i.test(content) &&
          /\b(phone|text|call|number)\b/i.test(content)) {
        insights.push({ type: 'phone_consent', label: 'Phone consent given' });
      }

      if (/(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/.test(message.content)) {
        insights.push({ type: 'phone', label: 'Phone number provided' });
      }
    }

    // Mark conversion point
    if (index === conversionPoint) {
      insights.push({ type: 'conversion', label: 'Lead created!' });
    }

    return insights;
  };

  const scrollToMessage = (index: number) => {
    setHighlightedMessage(index);
    setTimeout(() => setHighlightedMessage(null), 3000);
  };

  useEffect(() => {
    // Auto-scroll to conversion point if it exists
    if (conversionPoint !== null && timelineRef.current) {
      const conversionElement = timelineRef.current.querySelector(`[data-message-index="${conversionPoint}"]`);
      if (conversionElement) {
        conversionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessage(conversionPoint);
        setTimeout(() => setHighlightedMessage(null), 5000);
      }
    }
  }, [conversionPoint]);

  if (!messages || messages.length === 0) {
    return (
      <div className="text-center py-8">
        <ComputerDesktopIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">No messages in this conversation</p>
      </div>
    );
  }

  return (
    <div>
      {/* Timeline Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Message Timeline</h4>
          <p className="text-sm text-gray-500">
            {messages.filter(m => m.role === 'user').length} user messages • {messages.filter(m => m.role === 'assistant').length} bot responses • {conversation.duration ? `${Math.round(conversation.duration)} minutes` : 'Ongoing'}
          </p>
        </div>

        {conversionPoint !== null && (
          <button
            onClick={() => scrollToMessage(conversionPoint)}
            className="flex items-center space-x-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
          >
            <StarIcon className="h-4 w-4" />
            <span>Jump to Conversion</span>
          </button>
        )}
      </div>

      {/* Messages Timeline */}
      <div ref={timelineRef} className="space-y-6 max-h-96 overflow-y-auto">
        {messages.map((message, index) => {
          const insights = getMessageInsights(message, index);
          const isHighlighted = highlightedMessage === index;
          const isConversionPoint = index === conversionPoint;

          return (
            <div
              key={index}
              data-message-index={index}
              className={`relative flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} transition-all duration-300 ${
                isHighlighted ? 'transform scale-105' : ''
              }`}
            >
              {/* Timeline line for assistant messages */}
              {message.role === 'assistant' && index < messages.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-6 bg-gray-200"></div>
              )}

              <div className={`flex items-start space-x-3 max-w-lg ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                {/* Avatar */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.role === 'user'
                    ? 'bg-blue-500'
                    : 'bg-gray-500'
                } ${isConversionPoint ? 'ring-4 ring-green-300 ring-opacity-50' : ''}`}>
                  {message.role === 'user' ? (
                    <UserIcon className="h-5 w-5 text-white" />
                  ) : (
                    <ComputerDesktopIcon className="h-5 w-5 text-white" />
                  )}
                </div>

                {/* Message Content */}
                <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`inline-block p-3 rounded-lg max-w-full ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-900'
                  } ${isHighlighted ? 'ring-2 ring-yellow-400 ring-opacity-75' : ''} ${
                    isConversionPoint ? 'ring-2 ring-green-400' : ''
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>

                  {/* Timestamp */}
                  <div className={`flex items-center mt-1 text-xs text-gray-500 ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}>
                    <ClockIcon className="h-3 w-3 mr-1" />
                    {formatTimestamp(message.timestamp)}
                  </div>

                  {/* Insights */}
                  {insights.length > 0 && (
                    <div className={`mt-2 space-y-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                      {insights.map((insight, insightIndex) => (
                        <div
                          key={insightIndex}
                          className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                            insight.type === 'conversion'
                              ? 'bg-green-100 text-green-800'
                              : insight.type === 'email' || insight.type === 'phone'
                              ? 'bg-blue-100 text-blue-800'
                              : insight.type === 'name'
                              ? 'bg-purple-100 text-purple-800'
                              : insight.type === 'special_needs'
                              ? 'bg-orange-100 text-orange-800'
                              : insight.type === 'child_info'
                              ? 'bg-pink-100 text-pink-800'
                              : insight.type === 'phone_consent'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          } mr-1 mb-1`}
                        >
                          {insight.type === 'conversion' ? (
                            <CheckCircleIcon className="h-3 w-3" />
                          ) : insight.type === 'special_needs' ? (
                            <ExclamationTriangleIcon className="h-3 w-3" />
                          ) : (
                            <CheckCircleIcon className="h-3 w-3" />
                          )}
                          <span>{insight.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversation Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Conversation Summary</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Total Messages</p>
            <p className="font-medium">{messages.length}</p>
            <p className="text-xs text-gray-400 mt-1">
              {Math.floor(messages.length / 2)} exchanges
            </p>
          </div>
          <div>
            <p className="text-gray-500">User Messages</p>
            <p className="font-medium text-blue-600">{messages.filter(m => m.role === 'user').length}</p>
            <p className="text-xs text-gray-400 mt-1">
              {((messages.filter(m => m.role === 'user').length / messages.length) * 100).toFixed(0)}% of total
            </p>
          </div>
          <div>
            <p className="text-gray-500">Bot Responses</p>
            <p className="font-medium text-purple-600">{messages.filter(m => m.role === 'assistant').length}</p>
            <p className="text-xs text-gray-400 mt-1">
              {((messages.filter(m => m.role === 'assistant').length / messages.length) * 100).toFixed(0)}% of total
            </p>
          </div>
          <div>
            <p className="text-gray-500">Conversion</p>
            <p className={`font-medium ${conversation.lead_id ? 'text-green-600' : 'text-red-600'}`}>
              {conversation.lead_id ? 'Yes' : 'No'}
            </p>
            {conversation.lead_id && (
              <p className="text-xs text-gray-400 mt-1">Lead #{conversation.lead_id}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageTimeline;